// ==========================================
// Restock Alert – Shared Utilities
// ==========================================

const KLAVIYO_API_URL = 'https://a.klaviyo.com/client/back-in-stock-subscriptions/';
const LS_PREFIX = 'mh_bis_';

function isSubscribed(variantId) {
  return localStorage.getItem(LS_PREFIX + variantId) === 'true';
}

function markSubscribed(variantId) {
  localStorage.setItem(LS_PREFIX + variantId, 'true');
}

function validateEmail(email) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
}

async function submitSubscription(email, variantId, apiKey) {
  const res = await fetch(`${KLAVIYO_API_URL}?company_id=${apiKey}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'revision': '2024-10-15',
    },
    body: JSON.stringify({
      data: {
        type: 'back-in-stock-subscription',
        attributes: {
          channels: ['EMAIL'],
          profile: {
            data: {
              type: 'profile',
              attributes: { email },
            },
          },
        },
        relationships: {
          variant: {
            data: {
              type: 'catalog-variant',
              id: `$shopify:::$default:::${variantId}`,
            },
          },
        },
      },
    }),
  });

  if (res.status === 202 || res.status === 409) return;
  throw new Error('Subscription failed: ' + res.status);
}

// ==========================================
// Restock Alert – PDP Custom Element
// ==========================================

if (!customElements.get('restock-alert')) {
  customElements.define('restock-alert', class RestockAlert extends HTMLElement {
    connectedCallback() {
      const apiKey = this.dataset.klaviyoKey;
      const i18n = {
        notify:       this.dataset.i18nNotify,
        notified:     this.dataset.i18nNotified,
        submit:       this.dataset.i18nSubmit,
        saving:       this.dataset.i18nSaving,
        emailInvalid: this.dataset.i18nEmailInvalid,
        error:        this.dataset.i18nError,
      };

      const trigger    = this.querySelector('.restock-alert__trigger');
      const panel      = this.querySelector('.restock-alert__panel');
      const emailInput = this.querySelector('.restock-alert__email');
      const submitBtn  = this.querySelector('.restock-alert__submit');
      const msg        = this.querySelector('.restock-alert__message');

      const getVariantId = () => this.dataset.variantId;

      const reset = () => {
        panel.hidden = true;
        msg.hidden = true;
        emailInput.value = '';
        trigger.textContent = i18n.notify;
        trigger.disabled = false;
        submitBtn.disabled = false;
        submitBtn.textContent = i18n.submit;
      };

      const showSubscribed = () => {
        trigger.textContent = i18n.notified;
        trigger.disabled = true;
        panel.hidden = true;
      };

      if (isSubscribed(getVariantId())) showSubscribed();

      trigger.addEventListener('click', () => {
        panel.hidden = false;
        emailInput.focus();
      });

      submitBtn.addEventListener('click', async () => {
        const email = emailInput.value.trim();
        if (!validateEmail(email)) {
          msg.textContent = i18n.emailInvalid;
          msg.className = 'restock-alert__message restock-alert__message--error';
          msg.hidden = false;
          return;
        }

        submitBtn.disabled = true;
        submitBtn.textContent = i18n.saving;

        try {
          await submitSubscription(email, getVariantId(), apiKey);
          markSubscribed(getVariantId());
          showSubscribed();
        } catch {
          submitBtn.disabled = false;
          submitBtn.textContent = i18n.submit;
          msg.textContent = i18n.error;
          msg.className = 'restock-alert__message restock-alert__message--error';
          msg.hidden = false;
        }
      });

      document.addEventListener('variant:changed', (e) => {
        const variant = e.detail?.variant;
        if (!variant) return;
        reset();
        this.dataset.variantId = String(variant.id);
        if (!variant.available) {
          this.hidden = false;
          if (isSubscribed(String(variant.id))) showSubscribed();
        } else {
          this.hidden = true;
        }
      });
    }
  });
}

// ==========================================
// Restock Alert – PLP Modal
// ==========================================

class RestockModal {
  constructor() {
    const raw = window.restockI18n || {};
    const dec = document.createElement('textarea');
    this._i18n = Object.fromEntries(
      Object.entries(raw).map(([k, v]) => { dec.innerHTML = v; return [k, dec.value]; })
    );
    this._build();
    this._bindGlobal();
  }

  _build() {
    const i = this._i18n;
    const el = document.createElement('div');
    el.className = 'restock-modal';
    el.setAttribute('role', 'dialog');
    el.setAttribute('aria-modal', 'true');
    el.setAttribute('aria-label', i.modalAria || '');
    el.hidden = true;
    el.innerHTML = `
      <div class="restock-modal__overlay"></div>
      <div class="restock-modal__box">
        <button class="restock-modal__close" type="button" aria-label="${i.close || ''}">
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
            <path d="M1 1L15 15M15 1L1 15" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
          </svg>
        </button>
        <p class="restock-modal__eyebrow">${i.outOfStock || ''}</p>
        <h3 class="restock-modal__title"></h3>
        <p class="restock-modal__subtitle">${i.modalSubtitle || ''}</p>
        <div class="restock-modal__fields">
          <input type="email" class="restock-modal__email form-control" placeholder="${i.emailPlaceholder || ''}" autocomplete="email">
          <button type="button" class="restock-modal__submit btn btn--primary" style="width:100%">${i.submit || ''}</button>
        </div>
        <p class="restock-modal__message" hidden></p>
        <p class="restock-modal__legal">
          ${i.legal || ''}
          <a href="${i.privacyUrl || '#'}">${i.privacy || ''}</a>
        </p>
      </div>
    `;

    document.body.appendChild(el);
    this.el         = el;
    this.titleEl    = el.querySelector('.restock-modal__title');
    this.emailEl    = el.querySelector('.restock-modal__email');
    this.submitEl   = el.querySelector('.restock-modal__submit');
    this.messageEl  = el.querySelector('.restock-modal__message');

    el.querySelector('.restock-modal__overlay').addEventListener('click', () => this._close());
    el.querySelector('.restock-modal__close').addEventListener('click',   () => this._close());
    this.submitEl.addEventListener('click', () => this._submit());
    this.emailEl.addEventListener('keydown', (e) => { if (e.key === 'Enter') this._submit(); });
    document.addEventListener('keydown', (e) => { if (e.key === 'Escape' && !this.el.hidden) this._close(); });
  }

  _bindGlobal() {
    document.addEventListener('click', (e) => {
      const btn = e.target.closest('.restock-alert-plp__trigger');
      if (btn) this._open(btn);
    });
  }

  _open(trigger) {
    const i = this._i18n;
    this._trigger   = trigger;
    this._variantId = trigger.dataset.variantId;
    this._apiKey    = trigger.dataset.klaviyoKey;

    this.titleEl.textContent = trigger.dataset.productTitle;
    this.emailEl.value = '';
    this.messageEl.hidden = true;
    this.emailEl.style.display = '';
    this.submitEl.style.display = '';
    this.submitEl.disabled = false;
    this.submitEl.textContent = i.submit || '';

    if (isSubscribed(this._variantId)) {
      this.emailEl.style.display  = 'none';
      this.submitEl.style.display = 'none';
      this.messageEl.textContent  = i.alreadySubscribed || '';
      this.messageEl.className    = 'restock-modal__message restock-modal__message--success';
      this.messageEl.hidden       = false;
    }

    this.el.hidden = false;
    document.body.classList.add('restock-modal-open');
    requestAnimationFrame(() => this.emailEl.focus());
  }

  _close() {
    this.el.hidden = true;
    document.body.classList.remove('restock-modal-open');
    this._trigger?.focus();
  }

  async _submit() {
    const i = this._i18n;
    const email = this.emailEl.value.trim();
    if (!validateEmail(email)) {
      this.messageEl.textContent = i.emailInvalid || '';
      this.messageEl.className   = 'restock-modal__message restock-modal__message--error';
      this.messageEl.hidden      = false;
      return;
    }

    this.submitEl.disabled     = true;
    this.submitEl.textContent  = i.saving || '';

    try {
      await submitSubscription(email, this._variantId, this._apiKey);
      markSubscribed(this._variantId);

      this.emailEl.style.display  = 'none';
      this.submitEl.style.display = 'none';
      this.messageEl.textContent  = i.success || '';
      this.messageEl.className    = 'restock-modal__message restock-modal__message--success';
      this.messageEl.hidden       = false;

      if (this._trigger) {
        this._trigger.textContent = i.notified || '';
        this._trigger.disabled    = true;
      }

      setTimeout(() => this._close(), 10000);
    } catch {
      this.submitEl.disabled    = false;
      this.submitEl.textContent = i.submit || '';
      this.messageEl.textContent = i.error || '';
      this.messageEl.className   = 'restock-modal__message restock-modal__message--error';
      this.messageEl.hidden      = false;
    }
  }
}

document.addEventListener('DOMContentLoaded', () => new RestockModal());
